/**
 * 3D Graph View Component
 * Renders ideas in a Three.js 3D space using semantic embeddings for positioning
 * 
 * Visual Hierarchy:
 * - RANK → Size + Glow intensity (most important ideas are larger and brighter)
 * - STATUS → Animation state + Opacity (open=breathing, partial=pulsing, done=solid, abandoned=dim)
 * - CATEGORY → Hue (color coding)
 * - POSITION → Semantic meaning (embedding-driven placement)
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import * as THREE from 'three'
import { CSS2DRenderer, CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js'
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js'
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js'
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js'
import { RTSCameraControls } from '../lib/RTSCameraControls'
import type { Idea, AnchorInfo } from '../services/types'
import { getAnchors } from '../services/ideas'

// ============================================================================
// POST-PROCESSING SHADERS
// ============================================================================

/**
 * Chromatic Aberration Shader
 * Creates subtle RGB color fringing at edges - classic sci-fi lens effect
 */
const ChromaticAberrationShader = {
  uniforms: {
    tDiffuse: { value: null },
    amount: { value: 0.0025 }, // Subtle fringing
    angle: { value: 0.0 }, // Radial from center
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float amount;
    varying vec2 vUv;
    
    void main() {
      vec2 center = vec2(0.5);
      vec2 dir = vUv - center;
      float dist = length(dir);
      
      // Increase effect toward edges
      vec2 offset = dir * amount * dist;
      
      // Sample RGB at slightly different positions
      float r = texture2D(tDiffuse, vUv + offset).r;
      float g = texture2D(tDiffuse, vUv).g;
      float b = texture2D(tDiffuse, vUv - offset).b;
      float a = texture2D(tDiffuse, vUv).a;
      
      gl_FragColor = vec4(r, g, b, a);
    }
  `,
}

/**
 * Vignette Shader
 * Darkens edges to focus attention on the center where ideas cluster
 */
const VignetteShader = {
  uniforms: {
    tDiffuse: { value: null },
    offset: { value: 1.0 },    // How far from center the darkening starts
    darkness: { value: 1.0 },  // How dark the edges get
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float offset;
    uniform float darkness;
    varying vec2 vUv;
    
    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      
      // Distance from center (0 at center, ~0.7 at corners)
      vec2 center = vUv - 0.5;
      float dist = length(center);
      
      // Smooth falloff
      float vignette = smoothstep(offset, offset - 0.5, dist * (darkness + offset));
      
      color.rgb *= vignette;
      gl_FragColor = color;
    }
  `,
}

/**
 * Film Grain Shader (DISABLED)
 * Adds subtle noise texture - breaks up banding, feels more organic
 */
const _FilmGrainShader = {
  uniforms: {
    tDiffuse: { value: null },
    time: { value: 0.0 },
    intensity: { value: 0.08 }, // Subtle grain
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float time;
    uniform float intensity;
    varying vec2 vUv;
    
    // Simple noise function
    float rand(vec2 co) {
      return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
    }
    
    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      
      // Animated grain
      float grain = rand(vUv + time) * 2.0 - 1.0;
      
      // Apply grain, stronger in darker areas
      float luminance = dot(color.rgb, vec3(0.299, 0.587, 0.114));
      float grainAmount = intensity * (1.0 - luminance * 0.5);
      
      color.rgb += grain * grainAmount;
      gl_FragColor = color;
    }
  `,
}

/**
 * Scanline Shader (DISABLED)
 * Moving horizontal lines - classic hologram/CRT effect
 */
const _ScanlineShader = {
  uniforms: {
    tDiffuse: { value: null },
    time: { value: 0.0 },
    count: { value: 800.0 },    // Number of scanlines
    intensity: { value: 0.03 }, // Very subtle
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float time;
    uniform float count;
    uniform float intensity;
    varying vec2 vUv;
    
    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      
      // Slowly scrolling scanlines
      float scanline = sin((vUv.y + time * 0.05) * count) * 0.5 + 0.5;
      scanline = pow(scanline, 1.5); // Sharpen the lines
      
      color.rgb -= scanline * intensity;
      gl_FragColor = color;
    }
  `,
}

// ============================================================================
// FRESNEL MATERIAL FOR HOLOGRAPHIC NODES
// ============================================================================

/**
 * Creates a fresnel shader material for holographic node effect
 * Edges glow brighter, centers are more transparent
 */
function createFresnelMaterial(
  baseColor: number,
  emission: number,
  opacity: number
): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      baseColor: { value: new THREE.Color(baseColor) },
      emission: { value: emission },
      opacity: { value: opacity },
      time: { value: 0.0 },
    },
    vertexShader: /* glsl */ `
      varying vec3 vNormal;
      varying vec3 vViewDir;
      varying vec3 vWorldPos;
      
      void main() {
        vNormal = normalize(normalMatrix * normal);
        vec4 worldPos = modelMatrix * vec4(position, 1.0);
        vWorldPos = worldPos.xyz;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vViewDir = normalize(-mvPosition.xyz);
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 baseColor;
      uniform float emission;
      uniform float opacity;
      uniform float time;
      
      varying vec3 vNormal;
      varying vec3 vViewDir;
      varying vec3 vWorldPos;
      
      void main() {
        // Fresnel effect - edges glow more
        float fresnel = pow(1.0 - abs(dot(vNormal, vViewDir)), 2.5);
        
        // Core glow with subtle pulse
        float pulse = 1.0 + sin(time * 2.0) * 0.05;
        
        // Color: base + fresnel boost + emission
        vec3 color = baseColor * (0.5 + fresnel * emission * pulse);
        
        // Alpha: more transparent in center, opaque at edges
        float alpha = mix(opacity * 0.4, opacity, fresnel);
        
        gl_FragColor = vec4(color, alpha);
      }
    `,
    transparent: true,
    side: THREE.FrontSide,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  })
}

interface GraphView3DProps {
  ideas: Idea[]
  onIdeaClick: (idea: Idea) => void
}

// Anchor label type
type AnchorLabelData = { 
  label: string
  position: [number, number, number]
  isPrimary: boolean
  axis: string
  direction: 'positive' | 'negative'
}
type AnchorLabelsMap = Record<string, AnchorLabelData>

// Default anchor positions (used before API data loads)
// Primary axes shown as main beacons, secondary as smaller landmarks
const DEFAULT_ANCHOR_LABELS: AnchorLabelsMap = {
  // Primary axes (X, Y, Z)
  technical: { label: 'Technical', position: [8, 0, 0], isPrimary: true, axis: 'x', direction: 'positive' },
  creative: { label: 'Creative', position: [-8, 0, 0], isPrimary: true, axis: 'x', direction: 'negative' },
  business: { label: 'Business', position: [0, 8, 0], isPrimary: true, axis: 'y', direction: 'positive' },
  personal: { label: 'Personal', position: [0, -8, 0], isPrimary: true, axis: 'y', direction: 'negative' },
  execution: { label: 'Execution', position: [0, 0, 8], isPrimary: true, axis: 'z', direction: 'positive' },
  exploration: { label: 'Exploration', position: [0, 0, -8], isPrimary: true, axis: 'z', direction: 'negative' },
  // Secondary axes (landmarks)
  collaborative: { label: 'Collaborative', position: [4, 4, 4], isPrimary: false, axis: 'social', direction: 'positive' },
  solo: { label: 'Solo', position: [-4, -4, -4], isPrimary: false, axis: 'social', direction: 'negative' },
  commercial: { label: 'Commercial', position: [4, 6, 2], isPrimary: false, axis: 'revenue', direction: 'positive' },
  opensource: { label: 'Open Source', position: [-4, -2, -4], isPrimary: false, axis: 'revenue', direction: 'negative' },
  novel: { label: 'Novel', position: [-2, 2, -6], isPrimary: false, axis: 'innovation', direction: 'positive' },
  traditional: { label: 'Traditional', position: [2, 0, 4], isPrimary: false, axis: 'innovation', direction: 'negative' },
}

// Convert API anchors to the format expected by the component
function convertAnchorsToLabels(
  anchors: Record<string, AnchorInfo> | null
): AnchorLabelsMap {
  if (!anchors) return DEFAULT_ANCHOR_LABELS

  const result: AnchorLabelsMap = {}
  for (const [id, anchor] of Object.entries(anchors)) {
    result[id] = {
      label: anchor.label,
      position: anchor.position,
      isPrimary: anchor.isPrimary,
      axis: anchor.axis,
      direction: anchor.direction,
    }
  }
  return result
}

// Category colors matching the existing design system
const CATEGORY_COLORS = [
  0x00f0ff, // energy-cyan
  0x8f00ff, // weave-300
  0xf59e0b, // alert-amber
  0x22c55e, // alert-success
  0xa855f7, // weave-200
  0x06b6d4, // energy-turquoise
]

// Status configuration for visual encoding
const STATUS_CONFIG = {
  open: {
    opacity: 0.9,
    bobEnabled: true,
    bobSpeed: 2, // Gentle breathing
    pulseEnabled: false,
    rotationSpeed: 0.005,
    label: 'Open',
  },
  partial: {
    opacity: 0.9,
    bobEnabled: true,
    bobSpeed: 4, // Faster, working pulse
    pulseEnabled: true,
    rotationSpeed: 0.008,
    label: 'In Progress',
  },
  done: {
    opacity: 1.0,
    bobEnabled: false, // Solid, stable
    bobSpeed: 0,
    pulseEnabled: false,
    rotationSpeed: 0.003, // Slow, stable rotation
    label: 'Done',
  },
  abandoned: {
    opacity: 0.35,
    bobEnabled: false,
    bobSpeed: 0,
    pulseEnabled: false,
    rotationSpeed: 0.001, // Barely moving
    label: 'Abandoned',
  },
} as const

function getCategoryColor(category: string): number {
  let hash = 0
  for (let i = 0; i < category.length; i++) {
    hash = category.charCodeAt(i) + ((hash << 5) - hash)
  }
  return CATEGORY_COLORS[Math.abs(hash) % CATEGORY_COLORS.length]!
}

function getCategoryColorHex(category: string): string {
  const color = getCategoryColor(category)
  return '#' + color.toString(16).padStart(6, '0')
}

/**
 * Calculate node size based on rank (1-100)
 * Range: 0.2 (lowest rank) to 0.5 (highest rank)
 */
function getRankBasedSize(rank: number): number {
  const normalized = Math.max(1, Math.min(100, rank)) / 100
  return 0.2 + normalized * 0.3
}

/**
 * Calculate emission multiplier based on rank
 * Higher rank = brighter glow (picked up by bloom)
 */
function getRankBasedEmission(rank: number): number {
  const normalized = Math.max(1, Math.min(100, rank)) / 100
  // Range from 0.6 (dim) to 1.5 (bright, will bloom)
  return 0.6 + normalized * 0.9
}

/**
 * Brighten a color by a multiplier (for emission effect)
 * Kept for potential fallback to MeshBasicMaterial
 */
function _brightenColor(color: number, multiplier: number): number {
  const r = Math.min(255, ((color >> 16) & 255) * multiplier)
  const g = Math.min(255, ((color >> 8) & 255) * multiplier)
  const b = Math.min(255, (color & 255) * multiplier)
  return (Math.floor(r) << 16) | (Math.floor(g) << 8) | Math.floor(b)
}
// Exported for potential future use
void _brightenColor
void _FilmGrainShader
void _ScanlineShader

export function GraphView3D({ ideas, onIdeaClick }: GraphView3DProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const sceneRef = useRef<{
    scene: THREE.Scene
    camera: THREE.PerspectiveCamera
    renderer: THREE.WebGLRenderer
    labelRenderer: CSS2DRenderer
    composer: EffectComposer
    controls: RTSCameraControls
    ideaNodes: THREE.Mesh[]
    particles: THREE.Points
    axisStreams: THREE.Points[]
    animationId: number
    startTime: number
  } | null>(null)

  const [hoveredIdea, setHoveredIdea] = useState<{
    idea: Idea
    screenPosition: { x: number; y: number }
  } | null>(null)

  // State for anchor positions (fetched from API)
  const [anchorLabels, setAnchorLabels] = useState(DEFAULT_ANCHOR_LABELS)
  const anchorBeaconsRef = useRef<THREE.Group[]>([])

  // Fetch real anchor positions from API
  useEffect(() => {
    getAnchors()
      .then((response) => {
        if (response.initialized && response.anchors) {
          const converted = convertAnchorsToLabels(response.anchors)
          setAnchorLabels(converted)
        }
      })
      .catch((error) => {
        console.warn('Failed to fetch anchor positions, using defaults:', error)
      })
  }, [])

  // Calculate status counts for the info panel
  const statusCounts = ideas.reduce(
    (acc, idea) => {
      acc[idea.status] = (acc[idea.status] || 0) + 1
      return acc
    },
    {} as Record<string, number>
  )

  // Raycasting for hover detection
  const raycaster = useRef(new THREE.Raycaster())
  const mouse = useRef(new THREE.Vector2())

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!sceneRef.current) return

      // Use the renderer's canvas for accurate coordinates
      const canvas = sceneRef.current.renderer.domElement
      const rect = canvas.getBoundingClientRect()
      mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
      mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

      const { camera, ideaNodes } = sceneRef.current
      raycaster.current.setFromCamera(mouse.current, camera)
      const intersects = raycaster.current.intersectObjects(ideaNodes, false)

      if (intersects.length > 0) {
        const node = intersects[0]!.object
        const idea = node.userData.idea as Idea

        // Convert 3D position to screen coordinates
        const vector = node.position.clone().project(camera)
        const screenX = ((vector.x * 0.5 + 0.5) * rect.width) + rect.left
        const screenY = ((-vector.y * 0.5 + 0.5) * rect.height) + rect.top

        setHoveredIdea({
          idea,
          screenPosition: { x: screenX, y: screenY },
        })

        canvas.style.cursor = 'pointer'
      } else {
        setHoveredIdea(null)
        canvas.style.cursor = 'default'
      }
    },
    [ideas]
  )

  const handleClick = useCallback(
    (event: MouseEvent) => {
      if (!sceneRef.current) return

      // Use the renderer's canvas for accurate coordinates
      const canvas = sceneRef.current.renderer.domElement
      const rect = canvas.getBoundingClientRect()
      mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
      mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

      const { camera, ideaNodes } = sceneRef.current
      raycaster.current.setFromCamera(mouse.current, camera)
      const intersects = raycaster.current.intersectObjects(ideaNodes, false)

      if (intersects.length > 0) {
        const idea = intersects[0]!.object.userData.idea as Idea
        onIdeaClick(idea)
      }
    },
    [onIdeaClick]
  )

  // Initialize Three.js scene
  useEffect(() => {
    if (!containerRef.current) return

    const container = containerRef.current
    const width = container.clientWidth
    const height = container.clientHeight

    // Scene
    const scene = new THREE.Scene()
    scene.background = new THREE.Color(0x0f0524) // Void color

    // Camera
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000)
    camera.position.set(0, 5, 15)

    // WebGL Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.setSize(width, height)
    container.appendChild(renderer.domElement)

    // CSS2D Renderer for labels
    const labelRenderer = new CSS2DRenderer()
    labelRenderer.setSize(width, height)
    labelRenderer.domElement.style.position = 'absolute'
    labelRenderer.domElement.style.top = '0'
    labelRenderer.domElement.style.pointerEvents = 'none'
    container.appendChild(labelRenderer.domElement)

    // Post-processing pipeline
    // Order: Render → Bloom → Chromatic Aberration → Film Grain → Scanlines → Vignette → Output
    const composer = new EffectComposer(renderer)
    
    // 1. Base render
    composer.addPass(new RenderPass(scene, camera))
    
    // 2. Bloom - tuned for rank-based emission (high-rank ideas glow more)
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(width, height),
      0.7, // strength - visible glow on bright nodes
      0.5, // radius - wider bloom spread
      0.75 // threshold - catch bright nodes
    )
    composer.addPass(bloomPass)
    
    // 3. Chromatic Aberration - subtle sci-fi lens effect
    const chromaPass = new ShaderPass(ChromaticAberrationShader)
    ;(chromaPass.uniforms as typeof ChromaticAberrationShader.uniforms).amount.value = 0.003
    composer.addPass(chromaPass)
    
    // 4. Vignette - very subtle edge darkening
    const vignettePass = new ShaderPass(VignetteShader)
    ;(vignettePass.uniforms as typeof VignetteShader.uniforms).offset.value = 1.3  // Starts further out
    ;(vignettePass.uniforms as typeof VignetteShader.uniforms).darkness.value = 0.4 // Much gentler
    composer.addPass(vignettePass)
    
    // 7. Output - proper color space conversion
    composer.addPass(new OutputPass())

    // RTS Camera Controls
    const controls = new RTSCameraControls(camera, renderer.domElement)

    // Create axis lines
    const axisGroup = createAxisLines()
    scene.add(axisGroup)
    
    // Create axis particle streams (energy flowing along semantic axes)
    const axisStreams = [
      createAxisParticleStream('x', 0x00f0ff), // Cyan for X (Technical-Creative)
      createAxisParticleStream('y', 0x8f00ff), // Violet for Y (Business-Personal)
      createAxisParticleStream('z', 0x00f0ff), // Cyan for Z (Shipping-Exploration)
    ]
    axisStreams.forEach(stream => scene.add(stream))

    // Create anchor beacons (using default positions, will be updated when API data arrives)
    const anchorBeacons: THREE.Group[] = []
    Object.entries(DEFAULT_ANCHOR_LABELS).forEach(([_id, anchor]) => {
      const beacon = createAnchorBeacon(anchor.label, anchor.position, anchor.isPrimary)
      scene.add(beacon)
      anchorBeacons.push(beacon)
    })
    anchorBeaconsRef.current = anchorBeacons

    // Create background particles
    const particles = createBackgroundParticles()
    scene.add(particles)

    // Ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.3)
    scene.add(ambientLight)

    // Store refs
    sceneRef.current = {
      scene,
      camera,
      renderer,
      labelRenderer,
      composer,
      controls,
      ideaNodes: [],
      particles,
      axisStreams,
      animationId: 0,
      startTime: performance.now(),
    }

    // Animation loop with status-based behavior
    function animate() {
      const ref = sceneRef.current
      if (!ref) return

      ref.animationId = requestAnimationFrame(animate)
      
      // Use real elapsed time for consistent animation speed
      const elapsed = (performance.now() - ref.startTime) / 1000

      // Update controls
      ref.controls.update()

      // Animate particles (slow cosmic drift)
      ref.particles.rotation.y += 0.0002

      // Animate axis particle streams (energy flow)
      ref.axisStreams.forEach((stream, axisIndex) => {
        animateAxisParticleStream(stream, axisIndex as 0 | 1 | 2)
      })

      // Animate idea nodes based on their status
      ref.ideaNodes.forEach((node, i) => {
        const idea = node.userData.idea as Idea
        const config = STATUS_CONFIG[idea.status]
        const baseY = node.userData.baseY as number

        // Status-based bob animation
        if (config.bobEnabled && baseY !== undefined) {
          const bobAmount = config.pulseEnabled ? 0.08 : 0.04 // Partial status bobs more
          node.position.y = baseY + Math.sin(elapsed * config.bobSpeed + i * 0.5) * bobAmount
        }

        // Status-based rotation speed
        node.rotation.y += config.rotationSpeed

        // Update fresnel material time uniform (for subtle pulse)
        if (node.material instanceof THREE.ShaderMaterial && node.material.uniforms.time) {
          node.material.uniforms.time.value = elapsed
        }

        // Pulse effect for "partial" status (working on it) - for non-fresnel materials
        if (config.pulseEnabled && node.material instanceof THREE.MeshBasicMaterial) {
          const baseBrightness = node.userData.baseBrightness as number || 1
          const pulse = 0.85 + Math.sin(elapsed * 6 + i) * 0.15 // 6Hz pulse
          const color = new THREE.Color(node.userData.baseColor as number)
          color.multiplyScalar(baseBrightness * pulse)
          node.material.color = color
        }
      })

      // Render
      ref.composer.render()
      ref.labelRenderer.render(ref.scene, ref.camera)
    }
    animate()

    // Event listeners on the canvas element for accurate coordinates
    renderer.domElement.addEventListener('mousemove', handleMouseMove)
    renderer.domElement.addEventListener('click', handleClick)

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current || !sceneRef.current) return

      const w = containerRef.current.clientWidth
      const h = containerRef.current.clientHeight

      sceneRef.current.camera.aspect = w / h
      sceneRef.current.camera.updateProjectionMatrix()
      sceneRef.current.renderer.setSize(w, h)
      sceneRef.current.labelRenderer.setSize(w, h)
      sceneRef.current.composer.setSize(w, h)
    }
    window.addEventListener('resize', handleResize)

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize)
      renderer.domElement.removeEventListener('mousemove', handleMouseMove)
      renderer.domElement.removeEventListener('click', handleClick)

      if (sceneRef.current) {
        cancelAnimationFrame(sceneRef.current.animationId)
        sceneRef.current.controls.dispose()
        sceneRef.current.renderer.dispose()
        container.removeChild(sceneRef.current.renderer.domElement)
        container.removeChild(sceneRef.current.labelRenderer.domElement)
      }
    }
  }, [handleMouseMove, handleClick])

  // Update anchor beacon positions when anchorLabels changes (from API)
  useEffect(() => {
    if (!sceneRef.current || anchorBeaconsRef.current.length === 0) return

    const anchorIds = Object.keys(anchorLabels)
    anchorBeaconsRef.current.forEach((beacon, index) => {
      const anchorId = anchorIds[index]
      if (anchorId && anchorLabels[anchorId]) {
        const newPosition = anchorLabels[anchorId].position
        beacon.position.set(newPosition[0], newPosition[1], newPosition[2])
      }
    })
  }, [anchorLabels])

  // Update idea nodes when ideas change
  useEffect(() => {
    if (!sceneRef.current) return

    const { scene, ideaNodes } = sceneRef.current

    // Remove old idea nodes
    ideaNodes.forEach((node) => {
      scene.remove(node)
      node.geometry.dispose()
      if (node.material instanceof THREE.Material) {
        node.material.dispose()
      }
    })
    sceneRef.current.ideaNodes = []

    // Create new nodes
    ideas.forEach((idea) => {
      const node = createIdeaNode(idea)
      scene.add(node)
      sceneRef.current!.ideaNodes.push(node)
    })
  }, [ideas])

  return (
    <div className="relative w-full h-full">
      <div ref={containerRef} className="w-full h-full" />

      {/* Hover Card */}
      {hoveredIdea && (
        <div
          className="fixed pointer-events-none z-50"
          style={{
            left: hoveredIdea.screenPosition.x,
            top: hoveredIdea.screenPosition.y,
            transform: 'translate(-50%, -100%) translateY(-20px)',
          }}
        >
          <div className="glass-panel p-4 min-w-[240px] max-w-[320px] relative">
            {/* Top accent line */}
            <div
              className="absolute top-0 left-0 right-0 h-[2px]"
              style={{
                background: `linear-gradient(90deg, transparent, ${getCategoryColorHex(hoveredIdea.idea.category || 'default')}, transparent)`,
              }}
            />

            {/* Corner decorations */}
            <div
              className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2"
              style={{ borderColor: getCategoryColorHex(hoveredIdea.idea.category || 'default') }}
            />
            <div
              className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2"
              style={{ borderColor: getCategoryColorHex(hoveredIdea.idea.category || 'default') }}
            />

            {/* Content */}
            <div className="flex items-start gap-2 mb-2">
              <span className="rank-indicator text-xs flex-shrink-0">{hoveredIdea.idea.rank}</span>
              <h3 className="text-sm font-semibold text-[var(--chrome-100)]">
                {hoveredIdea.idea.name}
              </h3>
            </div>

            {hoveredIdea.idea.description && (
              <p className="text-[var(--chrome-400)] text-xs mb-2 line-clamp-2">
                {hoveredIdea.idea.description}
              </p>
            )}

            <div className="flex items-center justify-between text-[10px]">
              <span style={{ color: getCategoryColorHex(hoveredIdea.idea.category || 'default') }}>
                {hoveredIdea.idea.category || 'Uncategorized'}
              </span>
              <span className={`px-1.5 py-0.5 rounded ${
                hoveredIdea.idea.status === 'done' ? 'bg-green-500/20 text-green-400' :
                hoveredIdea.idea.status === 'partial' ? 'bg-amber-500/20 text-amber-400' :
                hoveredIdea.idea.status === 'abandoned' ? 'bg-red-500/20 text-red-400' :
                'bg-cyan-500/20 text-cyan-400'
              }`}>
                {STATUS_CONFIG[hoveredIdea.idea.status].label}
              </span>
            </div>
            
            <div className="mt-2 pt-2 border-t border-[var(--chrome-700)] text-[10px] text-[var(--chrome-500)]">
              Click to edit
            </div>
          </div>
        </div>
      )}

      {/* Idea Count Panel (top-right) */}
      <div className="absolute top-4 right-4 glass-panel p-3 text-xs">
        <div className="text-[var(--chrome-200)] font-semibold mb-2">
          {ideas.length} {ideas.length === 1 ? 'idea' : 'ideas'} in space
        </div>
        <div className="space-y-1 text-[10px]">
          {statusCounts.open ? (
            <div className="flex justify-between">
              <span className="text-cyan-400">Open</span>
              <span className="text-[var(--chrome-400)]">{statusCounts.open}</span>
            </div>
          ) : null}
          {statusCounts.partial ? (
            <div className="flex justify-between">
              <span className="text-amber-400">In Progress</span>
              <span className="text-[var(--chrome-400)]">{statusCounts.partial}</span>
            </div>
          ) : null}
          {statusCounts.done ? (
            <div className="flex justify-between">
              <span className="text-green-400">Done</span>
              <span className="text-[var(--chrome-400)]">{statusCounts.done}</span>
            </div>
          ) : null}
          {statusCounts.abandoned ? (
            <div className="flex justify-between">
              <span className="text-red-400/60">Abandoned</span>
              <span className="text-[var(--chrome-400)]">{statusCounts.abandoned}</span>
            </div>
          ) : null}
        </div>
      </div>

      {/* Legend / Controls hint */}
      <div className="absolute bottom-4 left-4 glass-panel p-3 text-xs text-[var(--chrome-400)]">
        <div className="mb-2 text-[var(--chrome-200)] font-semibold">Navigation</div>
        <div className="space-y-0.5">
          <div>Right-click drag: Rotate</div>
          <div>Shift + drag: Pan</div>
          <div>Scroll: Zoom</div>
        </div>
        <div className="mt-3 pt-2 border-t border-[var(--chrome-700)]">
          <div className="text-[var(--chrome-300)] mb-1">Visual Key</div>
          <div className="text-[10px] space-y-0.5">
            <div>Size = Importance (rank)</div>
            <div>Brightness = Priority</div>
            <div>Motion = Status</div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper functions to create 3D objects

function createIdeaNode(idea: Idea): THREE.Mesh {
  // Rank-based sizing: important ideas are larger
  const size = getRankBasedSize(idea.rank)
  const geometry = new THREE.OctahedronGeometry(size, 1) // Subdivide once for smoother fresnel

  // Category color with rank-based emission (brighter = more bloom)
  const baseColor = getCategoryColor(idea.category || 'default')
  const emission = getRankBasedEmission(idea.rank)

  // Status-based opacity
  const statusConfig = STATUS_CONFIG[idea.status]
  
  // Use Fresnel material for holographic edge-glow effect
  const material = createFresnelMaterial(baseColor, emission, statusConfig.opacity)

  const mesh = new THREE.Mesh(geometry, material)

  // Position from embedding projection, scaled to scene units
  const positionScale = 5
  if (idea.position3d) {
    mesh.position.set(
      idea.position3d.x * positionScale,
      idea.position3d.y * positionScale,
      idea.position3d.z * positionScale
    )
  } else {
    // Ideas without embeddings go to origin with slight random offset
    // Use a seeded random based on idea ID for consistency
    const seed = idea.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    const pseudoRandom = (n: number) => ((seed * (n + 1) * 9301 + 49297) % 233280) / 233280
    mesh.position.set(
      (pseudoRandom(0) - 0.5) * 1.5,
      (pseudoRandom(1) - 0.5) * 1.5,
      (pseudoRandom(2) - 0.5) * 1.5
    )
  }

  // Add slight random initial rotation for visual variety
  const rotSeed = idea.id.charCodeAt(0) || 0
  mesh.rotation.x = (rotSeed / 255) * Math.PI
  mesh.rotation.z = ((rotSeed * 7) % 255 / 255) * Math.PI

  // Store data for animation
  mesh.userData = {
    idea,
    baseY: mesh.position.y,
    baseColor: baseColor,
    baseBrightness: emission,
  }

  return mesh
}

function createAnchorBeacon(
  label: string, 
  position: [number, number, number],
  isPrimary: boolean = true
): THREE.Group {
  const group = new THREE.Group()

  // Different sizes and colors for primary vs secondary anchors
  const size = isPrimary ? 0.4 : 0.25
  const color = isPrimary ? 0x00f0ff : 0x8f00ff // Cyan for primary, violet for secondary
  const opacity = isPrimary ? 0.6 : 0.4

  const geometry = new THREE.IcosahedronGeometry(size, 1)
  const material = new THREE.MeshBasicMaterial({
    color,
    wireframe: true,
    transparent: true,
    opacity,
  })

  const mesh = new THREE.Mesh(geometry, material)
  group.add(mesh)

  // CSS2D Label (always faces camera)
  const labelDiv = document.createElement('div')
  labelDiv.textContent = label
  
  const colorHex = isPrimary ? '#00F0FF' : '#8F00FF'
  const fontSize = isPrimary ? '12px' : '10px'
  const borderOpacity = isPrimary ? '0.3' : '0.2'
  
  labelDiv.style.cssText = `
    color: ${colorHex};
    font-family: 'Inter', system-ui, sans-serif;
    font-size: ${fontSize};
    font-weight: ${isPrimary ? '600' : '500'};
    text-transform: uppercase;
    letter-spacing: ${isPrimary ? '2px' : '1px'};
    text-shadow: 0 0 10px ${colorHex}, 0 0 20px ${colorHex};
    padding: ${isPrimary ? '4px 8px' : '2px 6px'};
    background: rgba(15, 5, 36, ${isPrimary ? '0.8' : '0.7'});
    border: 1px solid rgba(${isPrimary ? '0, 240, 255' : '143, 0, 255'}, ${borderOpacity});
    border-radius: 2px;
  `

  const labelObj = new CSS2DObject(labelDiv)
  labelObj.position.set(0, isPrimary ? 0.8 : 0.5, 0)
  group.add(labelObj)

  group.position.set(...position)
  return group
}

function createAxisLines(): THREE.Group {
  const group = new THREE.Group()
  const material = new THREE.LineBasicMaterial({
    color: 0x8f00ff, // Weave violet
    transparent: true,
    opacity: 0.3,
  })

  // X axis (Technical - Creative)
  const xGeom = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(-10, 0, 0),
    new THREE.Vector3(10, 0, 0),
  ])
  group.add(new THREE.Line(xGeom, material))

  // Y axis (Business - Personal)
  const yGeom = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0, -10, 0),
    new THREE.Vector3(0, 10, 0),
  ])
  group.add(new THREE.Line(yGeom, material))

  // Z axis (Shipping - Exploration)
  const zGeom = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0, 0, -10),
    new THREE.Vector3(0, 0, 10),
  ])
  group.add(new THREE.Line(zGeom, material))

  return group
}

function createBackgroundParticles(): THREE.Points {
  // Reduced count for better performance while maintaining atmosphere
  const count = 1500
  const positions = new Float32Array(count * 3)

  for (let i = 0; i < count; i++) {
    positions[i * 3] = (Math.random() - 0.5) * 35
    positions[i * 3 + 1] = (Math.random() - 0.5) * 35
    positions[i * 3 + 2] = (Math.random() - 0.5) * 35
  }

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))

  const material = new THREE.PointsMaterial({
    color: 0x8f00ff,
    size: 0.025,
    transparent: true,
    opacity: 0.35,
    blending: THREE.AdditiveBlending,
  })

  return new THREE.Points(geometry, material)
}

/**
 * Create particle stream flowing along a semantic axis
 * Represents energy/data flowing through the Ideaverse
 */
function createAxisParticleStream(axis: 'x' | 'y' | 'z', color: number): THREE.Points {
  const count = 150
  const positions = new Float32Array(count * 3)
  const velocities = new Float32Array(count)
  const sizes = new Float32Array(count)

  for (let i = 0; i < count; i++) {
    // Position along the axis (-10 to 10)
    const t = (i / count) * 20 - 10
    
    // Random velocity for variety
    velocities[i] = 0.015 + Math.random() * 0.025
    
    // Random size for depth
    sizes[i] = 0.03 + Math.random() * 0.04

    // Position with slight offset perpendicular to axis
    const offset1 = (Math.random() - 0.5) * 0.4
    const offset2 = (Math.random() - 0.5) * 0.4

    if (axis === 'x') {
      positions[i * 3] = t
      positions[i * 3 + 1] = offset1
      positions[i * 3 + 2] = offset2
    } else if (axis === 'y') {
      positions[i * 3] = offset1
      positions[i * 3 + 1] = t
      positions[i * 3 + 2] = offset2
    } else {
      positions[i * 3] = offset1
      positions[i * 3 + 1] = offset2
      positions[i * 3 + 2] = t
    }
  }

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  geometry.setAttribute('velocity', new THREE.BufferAttribute(velocities, 1))
  geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1))

  const material = new THREE.PointsMaterial({
    color: color,
    size: 0.04,
    transparent: true,
    opacity: 0.5,
    blending: THREE.AdditiveBlending,
    sizeAttenuation: true,
  })

  const points = new THREE.Points(geometry, material)
  points.userData.axis = axis
  return points
}

/**
 * Animate particles flowing along their axis
 * Called each frame to move particles and wrap around
 */
function animateAxisParticleStream(stream: THREE.Points, axisIndex: 0 | 1 | 2): void {
  const positionAttr = stream.geometry.attributes.position
  const velocityAttr = stream.geometry.attributes.velocity
  
  if (!positionAttr || !velocityAttr) return
  
  const positions = positionAttr.array as Float32Array
  const velocities = velocityAttr.array as Float32Array
  const count = positions.length / 3

  for (let i = 0; i < count; i++) {
    const posIdx = i * 3 + axisIndex
    const velocity = velocities[i] ?? 0.02
    
    // Move particle along its axis
    positions[posIdx] = (positions[posIdx] ?? 0) + velocity

    // Wrap around when reaching the end
    if ((positions[posIdx] ?? 0) > 10) {
      positions[posIdx] = -10
    }
  }

  positionAttr.needsUpdate = true
}

export default GraphView3D
